import {action} from "mobx";
import React from "react";
import {observer_mgl_r3} from "../../../../../Utils/ThreeJS/General.js";
import {MapTool, Tool, ToolData, ToolInterceptSubresult} from "./@Tool.js";
import {store} from "../../../../../Store/index.js";
import {MapDragInfo} from "./Helpers/DragHelper.js";
import {Vec2, Vec3} from "../../../../../Utils/ThreeJS/VectorStructs.js";
import {O, RunInAction, RunInAction_Set} from "web-vcore";
import {GetOpenMapID} from "../../../../../Store/main/maps.js";
import {GetMapState, GetSelectedObjects} from "../../../../../Store/main/maps/$mapState.js";
import {GetMapObject, GetMapObjects} from "../../../../../Server/DB/mapObjects.js";
import {UpdateMapObject} from "../../../../../Server/Commands/MapObjectCommands.js";
import {GetObjAncestors, ResolveObjRotInWorld} from "../../../../../Server/DB/mapObjects/$mapObject.js";
import {Select, Text} from "react-vcomponents";
import {MaybeSnapPosChange, MaybeSnapWorldPos, RotateOffsetByRotation, TransformWorldOffsetToObjLocalPoint_UsingRotationOnly} from "../../../../../Utils/ThreeJS/SpaceUtils.js";
import {MapSurface_current} from "../../MapSurface.js";
import {observer_mgl} from "mobx-graphlink";
import {ignore} from "mobx-sync";

export enum MoveSpace {
	local = "local",
	global = "global",
}

export class ToolState_Move {
	@O accessor space = MoveSpace.global;
	@O @ignore accessor currentDrag: MapDragInfo|n;
	@action EndCurrentDrag() {
		if (this.currentDrag == null) return;
		this.currentDrag = null;
	}
}

export class Tool_Move extends Tool {
	toolType = MapTool.move;
	get ToolState() { return store.main.maps.tools.toolStates.move; }

	/** Returns the preview-frame (rotation 0 for global; else the selected objects' shared parent world-rotation),
	 * plus the (possibly snapped) mouse-delta in both that frame and world-space. */
	GetCurrentShiftInfo() {
		const state = this.ToolState;
		const drag = state.currentDrag;
		if (drag == null) return null;

		const startPos = drag.startPos_world.ToVec2();
		const currentPos = (drag.currentPos_world ?? drag.startPos_world).ToVec2();
		const posDiff_world = currentPos.Minus(startPos);

		// global: snap in world-space; local: snap in the shared frame if one exists, else show the raw delta (snap happens per-object)
		const frameRot = state.space == MoveSpace.local ? this.GetSelectionSharedParentRot() : 0;
		const posDiff_frame = frameRot != null ? TransformWorldOffsetToObjLocalPoint_UsingRotationOnly(posDiff_world, frameRot) : posDiff_world;
		const posDiff_frame_snapped = frameRot != null ? MaybeSnapPosChange(posDiff_frame) : posDiff_frame;
		const posDiff_world_snapped = frameRot != null ? RotateOffsetByRotation(posDiff_frame_snapped, frameRot) : posDiff_world;
		return {startPos, frameRot: frameRot ?? 0, posDiff_frame_snapped, posDiff_world_snapped};
	}

	/** Returns the parent world-rotation shared by all selected objects (ie. their local-space orientation); null if they differ or still loading. */
	GetSelectionSharedParentRot() {
		const mapID = GetOpenMapID.CatchBail(null);
		if (!mapID) return null;
		const mapObjects = GetMapObjects.CatchBail(null, mapID);
		const selectedObjects = GetSelectedObjects.CatchBail(null, mapID);
		if (mapObjects == null || selectedObjects == null) return null;

		const rotations = selectedObjects.map(obj=>{
			const parent = obj.parent ? mapObjects.find(a=>a.id == obj.parent) : null;
			return parent ? (ResolveObjRotInWorld.CatchBail(null, parent) ?? 0) : 0;
		});
		return rotations.every(r=>r == rotations[0]) ? (rotations[0] ?? 0) : null;
	}

	RenderInToolbox = observer_mgl(({data}: {data: ToolData.RenderInToolbox})=>{
		const state = this.ToolState;
		const drag = state.currentDrag;
		const posDiff_snapped = this.GetCurrentShiftInfo()?.posDiff_world_snapped ?? Vec2.zero;
		return <>
			<Select ml={5} displayType="button bar" options={[
				{name: "Local", value: MoveSpace.local},
				{name: "Global", value: MoveSpace.global},
			]} value={state.space} onChange={val=>RunInAction_Set(()=>state.space = val)}/>
			{drag && <Text ml={5}>{`Shift: ${posDiff_snapped.x.toFixed(1)}m, ${posDiff_snapped.y.toFixed(1)}m`}</Text>}
		</>;
	});
	RenderInScene = observer_mgl_r3(({data}: {data: ToolData.RenderInScene})=>{
		const {map, mapState} = data;
		return <>
		</>;
	});
	RenderInSceneOverlay = observer_mgl((props: {data: ToolData.RenderInSceneOverlay})=>{
		const state = this.ToolState;
		const drag = state.currentDrag;
		const mapControls_DOM = MapSurface_current?.mapControls?.DOM;
		if (drag == null || mapControls_DOM == null) return null;

		const {startPos, frameRot, posDiff_frame_snapped, posDiff_world_snapped} = this.GetCurrentShiftInfo()!;
		// rect is aligned to the frame, with start & snapped-current at opposite corners (so center = their midpoint)
		const center_screen = mapControls_DOM.WorldPosToClientPos(startPos.Plus(posDiff_world_snapped.Times(.5)));
		const pixelsPerMeter = 1 / mapControls_DOM.OnePixelAsMeters().x;
		const width = Math.abs(posDiff_frame_snapped.x) * pixelsPerMeter;
		const height = Math.abs(posDiff_frame_snapped.y) * pixelsPerMeter;
		return <div style={{
			position: "fixed",
			border: "1px solid white",
			pointerEvents: "none",
			zIndex: 100,
			left: `${center_screen.x - width / 2}px`,
			top: `${center_screen.y - height / 2}px`,
			width: `${width}px`,
			height: `${height}px`,
			transform: `rotate(${frameRot}deg)`,
			transformOrigin: "center center",
		}}/>;
	});

	Tool_OnDeactivate(data: ToolData.Tool_OnDeactivate): void {
		this.ToolState.EndCurrentDrag();
	}
	Document_OnKeyDown(data: ToolData.Document_OnKeyDown): ToolInterceptSubresult | void {
		if (data.event.key == "Escape") {
			this.ToolState.EndCurrentDrag();
			return {absorb: true};
		}
	}

	Scene_OnPointerDown(data: ToolData.Scene_OnPointerClick) {
		const {event, mousePos, mousePos_world, controls} = data;
		if (event.button != 0) return;

		const state = this.ToolState;
		if (state.currentDrag == null) {
			RunInAction("Move.Scene_OnPointerDown", ()=>{
				state.currentDrag = new MapDragInfo({
					button: event.button,
					startPos_mouse: mousePos,
					startPos_world: mousePos_world,
					currentPos_mouse: mousePos,
					currentPos_world: mousePos_world,
				});
			});
		}
	}

	Document_OnPointerMove(data: ToolData.Document_OnPointerMove) {
		const {event, mousePos, mousePos_world, controls} = data;
		const state = this.ToolState;
		const drag = state.currentDrag;
		if (drag) {
			RunInAction("Move.Document_OnPointerMove", ()=>{
				drag.currentPos_mouse = mousePos;
				drag.currentPos_world = mousePos_world;
			});
		}
	}

	Document_OnPointerUp(data: ToolData.Document_OnPointerUp) {
		const {event, mousePos_world, controls} = data;
		const state = this.ToolState;
		const drag = state.currentDrag;
		if (drag && drag?.button == event.button) {
			const startPos_world = drag.startPos_world.ToVec2();
			const space = state.space;
			// In global mode, snap the world-space mouse-delta. In local mode, snapping is done per-object
			// in the object's local space at the end (on newPos), so here we use the raw mouse-delta.
			const globalOffset = mousePos_world.ToVec2().Minus(startPos_world);
			const globalOffset_withPossibleSnap = space == MoveSpace.global ? MaybeSnapPosChange(globalOffset) : globalOffset;

			// effect
			/*drag.endPos_mouse = mousePos_snapped;
			drag.endPos_world = mousePos_world_snapped.ToVec3(0);*/
			(async()=>{
				const mapID = GetOpenMapID.CatchBail(null);
				if (!mapID) return;
				const mapState = GetMapState.CatchBail(null, mapID);
				if (mapState == null) return;
				const mapObjects = GetMapObjects.CatchBail(null, mapID);
				if (!mapObjects) return;

				const selectedObjects = GetSelectedObjects.CatchBail(null, mapID);
				if (selectedObjects == null) return;
				const selectedObjects_ancestors = selectedObjects.map(obj=>GetObjAncestors.CatchBail(null, obj));
				const selectedObjects_excludeRedundant = selectedObjects.filter((obj, i)=>{
					const objAncestors = selectedObjects_ancestors[i];
					if (objAncestors == null) return false; // if ancestors-list is loading, assume object should be included in the move
					// if this object is a descendent of another selected object, skip it
					if (objAncestors.some(a=>selectedObjects.some(b=>b.id == a.id))) return false;
					return true;
				});

				for (const obj of selectedObjects_excludeRedundant) {
					let newPos = new Vec3(obj.position);

					// if object has parent, apply rotation of ancestors to determine the final amount to change this object's local-position
					const parent = mapObjects.find(a=>a.id == obj.parent);
					if (parent) {
						const parentRot = ResolveObjRotInWorld(parent);
						const posChange_inObjLocalSpace = TransformWorldOffsetToObjLocalPoint_UsingRotationOnly(globalOffset_withPossibleSnap, parentRot);
						newPos = newPos.Plus(posChange_inObjLocalSpace.ToVec3(0));
					} else {
						newPos = newPos.Plus(globalOffset_withPossibleSnap.ToVec3(0));
					}

					// In local-space mode, apply snapping in the object's local space/grid.
					// (eg. if obj started at integer local-pos, with integer snapping, it always ends at an integer local-pos)
					if (space == MoveSpace.local) {
						newPos = MaybeSnapWorldPos(newPos.ToVec2()).ToVec3(newPos.z);
					}

					await new UpdateMapObject({id: obj.id, updates: {position: newPos}}).RunOnServer();
				}
			})();

			this.ToolState.EndCurrentDrag();
		}
	}
}